import * as _ from "lodash";
import * as toastr from "toastr";
import { handleAdderGroupTooltip } from "../../app";
import * as Models from "../../Models";
import { RoleList } from "../../Models/Auth";
import { EpcCalculation, EpcRequest, Market, MarketCap, Region, RegionPricing } from "../../Models/CostCalculation";
import { CustomerEvent } from "../../Models/Customer";
import {
  ContractAmendmentApiClient,
  CostCalculationApiClient,
  CustomerApiClient,
  MarketAssumedValuesApiClient,
  PricingApiClient,
  UserService,
} from "../../Services";
import { IPricingApiClient } from "../../Services/PricingApiClient";
import { Reference as Constants } from "../../Shared/Constants/Reference";

export abstract class BaseReconcileCtrl {
  public costCalculationModel: EpcRequest.IEpcCalculationRequest;

  public possibleMarkets: Market.IMarket[];
  public selectedMarketCaps: MarketCap.IMarketCap;

  public epcCalculationResponse: EpcCalculation.IEpcCalculationResult;
  public previousEpcCalc: Models.CostCalculation.EpcCalculation.IEpcCalculationResult;
  public reconcileEpc: EpcCalculation.IContractAssociatedEpcCalculationResult;
  public pendingEpc: EpcCalculation.IContractAssociatedEpcCalculationResult;

  public regionAssociatedToCustomer: Region.IRegion;
  public selectedRegionPricing: RegionPricing.IRegionPricing;
  public selectedEscalator: string;

  public regionPricingMonthlyPaymentMinimum: number;
  public regionPricingMonthlyPaymentMaximum: number;

  public contractAmendmentRateInProgress: boolean;
  public hasUploadedContractAmendmentDocumentType: boolean;

  public leaseWarningMessage: string;
  public pageLoaded: boolean;
  public hasReconcileWarningMessage: boolean;
  public hasAnnualProductionChanges: boolean;
  public selectedLeasePriceBy: string;
  public igsCustomerNumber: string;

  protected roleList: RoleList.RoleList;

  public roundedAnnualProdChangePercent = null;
  public suggestedMonthlyPayment = null;
  public signedAnnualProd = null;

  constructor(
    protected userService: UserService.IUserService,
    protected $state: any,
    protected costCalculationApiClient: CostCalculationApiClient.ICostCalculationApiClient,
    protected customerApiClient: CustomerApiClient.ICustomerApiClient,
    protected marketAssumedValuesApiClient: MarketAssumedValuesApiClient.IMarketAssumedValuesApiClient,
    protected contractAmendmentApiClient: ContractAmendmentApiClient.IContractAmendmentApiClient,
    protected messageBox: any,
    protected $timeout: angular.ITimeoutService,
    protected pricingApiClient: IPricingApiClient,
  ) {
    this.pageLoaded = false;
    this.selectedLeasePriceBy = "Monthly Payment";
    this.igsCustomerNumber = this.$state.params.igsCustomerNumber;
    this.roleList = new RoleList.RoleList();

    this.verifyCustomerHasSignedContract();
    this.verifyAgentHasReconcilePermissions();
    this.setupPage();
  }

  public calculateEpcNogLog = (formIsValid: boolean): void => {
    if (!this.validatePage(formIsValid)) {
      return;
    }
    const request = this.transformRequest(this.costCalculationModel);
    //need to set this so the backend knows what itc year to use, not smart enough through the noLog method

    this.costCalculationApiClient.calculateEpcNoLog(request).then((response: EpcCalculation.IEpcCalculationResult) => {
      this.epcCalculationResponse = response;
      handleAdderGroupTooltip();
    });
  };

  public calculateEpcAndReconcile = (formIsValid: boolean): void => {
    if (!this.validatePage(formIsValid)) {
      toastr.error(
        "Form is invalid. All fields must be filled out and System Size, Annual Production, and Rate must have values greater than 0.",
      );
      return;
    }

    const openBox = this.messageBox.open(
      "Reconcile Calculation",
      "Are you sure you want to submit this reconcile calculation?",
      [
        { label: "Submit", cssClass: "btn-primary", result: true },
        { label: "Cancel", cssClass: "btn-cancel", result: false },
      ],
    );

    openBox.then((result: boolean) => {
      if (result) {
        this.costCalculationApiClient
          .calculateEpcAndReconcileContractEpcCalculation(this.transformRequest(this.costCalculationModel))
          .then(
            (response) => {
              if (response.success) {
                this.onEpcReconciled();
              } else {
                const errorString = response.errors.reduce((strOne, strTwo) => strOne + strTwo + "\r\n");
                toastr.error(errorString);
              }
            },
            () => {
              toastr.error(
                "Something went wrong when attempting to reconcile. Please try again or contact IT if the problem continues.",
                "ERROR!",
              );
            },
          );
      }
    });
  };

  public cancelAmendment = (): void => {
    this.costCalculationApiClient.cancelContractAmendment(this.igsCustomerNumber).then(() => {
      this.setupPageCore();
    });
  };

  public calculationValuesHaveChanged = (): boolean => {
    if (
      this.costCalculationModel &&
      this.reconcileEpc &&
      this.costCalculationModel.rate === this.reconcileEpc.rate &&
      this.costCalculationModel.regionPricingId === this.reconcileEpc.regionPricingId &&
      this.costCalculationModel.annualProduction === this.reconcileEpc.annualProduction &&
      this.costCalculationModel.systemSize === this.reconcileEpc.systemSize &&
      this.costCalculationModel.monthlyPayment === this.reconcileEpc.monthlyPayment
    ) {
      return false;
    }
    return true;
  };

  public isLease = (): boolean => {
    return this.costCalculationModel.productCode.toLowerCase().includes("lease");
  };

  public returnToCustomerProfile = (): void => {
    this.$state.go("main.customerv2", { igsCustomerNumber: this.costCalculationModel.customerReferenceNumber });
  };

  public validatePage = (formIsValid: boolean): boolean => {
    return (
      formIsValid &&
      this.costCalculationModel.systemSize !== 0 &&
      this.costCalculationModel.annualProduction !== 0 &&
      this.costCalculationModel.rate !== 0 &&
      this.validateYieldCap() &&
      this.isValidLeaseCalculation() &&
      this.validate()
    );
  };

  // see commentary on same method in the sandbox.ts - alose we could encapsulate
  // shared methods in a library (aka service) shared by all the cost calculation pages
  public onSelectedRegionPricingChange = (formIsValid: boolean): void => {
    var adderGroupCode = this.regionAssociatedToCustomer.customerAdderGroupCode;
    var regionPricings = this.regionAssociatedToCustomer.regionPricings.filter(
      (i: any) => i.escalator === parseFloat(this.selectedEscalator) && i.productTypeCode === this.costCalculationModel.productCode,
    );
    if (regionPricings.length === 0) {
      toastr.error("No pricing records found.");
    } else {
      var regionItcPricings = _.sortBy(
        _.flatten(regionPricings.map((i: any) => i.regionItcPricings)).filter(
          (i: any) => i.adderGroupCode === adderGroupCode,
        ),
        (i: any) => i.regionItcPricingId,
      ).reverse();

      if (regionItcPricings.length > 0) {
        var id = regionItcPricings[0].regionItcPricingId;
        this.selectedRegionPricing = this.regionAssociatedToCustomer.regionPricings.filter(
          (i: any) => i.regionItcPricings.filter((x: any) => x.regionItcPricingId === id).length > 0,
        )[0];

        this.costCalculationModel.regionPricingId = this.selectedRegionPricing.regionPricingId;
        // angularjs issue with md-slider, timeout is a workaround for it. explanation found here: https://github.com/angular/material/issues/9125
        if (
          this.costCalculationModel.rate !== 0 &&
          this.costCalculationModel.rate < this.selectedRegionPricing.rateCap
        ) {
          this.$timeout(this.updateContractRate, 0);
        } else {
          this.updateContractRate();
        }

        this.calculateEpcNogLog(formIsValid);
      }
    }
  };

  public updateContractRate = (): void => {
    this.costCalculationModel.rate = this.selectedRegionPricing.rateCap;
  };

  public getMonthlyPaymentFloor = (): number => {
    const value = (this.selectedRegionPricing.rateCap * this.costCalculationModel.annualProduction) / 12;
    return Math.floor(value * 100) / 100;
  };

  public getMonthlyPaymentCeil = (): number => {
    const value = (this.selectedRegionPricing.rateFloor * this.costCalculationModel.annualProduction) / 12;
    return Math.ceil(value * 100) / 100;
  };

  protected abstract validate(): boolean;
  protected abstract onEpcReconciled(): void;

  protected setupPage(): void {
    this.setupPageCore();
  }

  protected populateCostCalculationModel(response: EpcCalculation.IEpcCalculationResult): void {
    this.possibleMarkets = [];
    this.possibleMarkets.push(response.market);

    this.costCalculationModel.customerReferenceNumber = this.igsCustomerNumber;
    this.costCalculationModel.market = response.market;
    this.costCalculationModel.annualProduction = response.annualProduction;
    this.costCalculationModel.systemSize = response.systemSize;
    this.costCalculationModel.calculationStartDate = response.calculationStartDate;

    this.selectedRegionPricing = _.find(
      this.regionAssociatedToCustomer.regionPricings,
      (rp) => rp.regionPricingId === response.regionPricingId,
    );

    if (!this.selectedRegionPricing) {
      // Get region pricing associated with contract to replace more recent pricing for same escalator
      this.pricingApiClient
        .getRegionPricing(response.regionPricingId)
        .then((rp: RegionPricing.IRegionPricing) => {
          this.selectedRegionPricing = rp;
          
          this.regionAssociatedToCustomer.regionPricings = this.regionAssociatedToCustomer.regionPricings.filter(
            (i: any) => i.escalator !== rp.escalator,
          );
          this.regionAssociatedToCustomer.regionPricings.push(rp);
        })
    }

    this.costCalculationModel.regionPricingId = response.regionPricingId;
    this.costCalculationModel.rate = response.rate;
    this.costCalculationModel.productCode = response.productCode;
    this.costCalculationModel.productName = response.productName;
    this.costCalculationModel.monthlyPayment = response.monthlyPayment;
    this.selectedEscalator = response.escalator.value.toString();

    this.calculateEpcNogLog(true);
  }

  protected transformRequest(
    costCalculationModel: EpcRequest.IEpcCalculationRequest,
  ): EpcRequest.IEpcCalculationRequest {
    return {
      market: costCalculationModel.market,
      regionPricingId: costCalculationModel.regionPricingId,
      annualProduction: costCalculationModel.annualProduction,
      systemSize: costCalculationModel.systemSize,
      rate:
        this.selectedLeasePriceBy === "Rate" || costCalculationModel.productCode === "PPA"
          ? costCalculationModel.rate
          : 0,
      monthlyPayment:
        this.selectedLeasePriceBy === "Monthly Payment" && costCalculationModel.productCode !== "PPA"
          ? costCalculationModel.monthlyPayment
          : 0,
      calculationStartDate: costCalculationModel.calculationStartDate,
      customerReferenceNumber: costCalculationModel.customerReferenceNumber,
      partnerSystemNumber: costCalculationModel.partnerSystemNumber,
      hasAmendedContract: costCalculationModel.hasAmendedContract,
      unshadedPercent: costCalculationModel.unshadedPercent,
      productCode: costCalculationModel.productCode,
      isSystemDataOverridden: costCalculationModel.isSystemDataOverridden,
      productName: costCalculationModel.productName,
    };
  }

  protected onPageLoaded() {
    if (this.reconcileEpc.annualProdChangePercent != undefined) {
      this.resolveRequiredAmendmentInfo();
    }
  }

  private resolveRequiredAmendmentInfo() {
    this.roundedAnnualProdChangePercent = this.reconcileEpc.annualProdChangePercent.toFixed();
    this.suggestedMonthlyPayment = (
      this.reconcileEpc.monthlyPayment *
      (1 - this.reconcileEpc.annualProdChangePercent / 100)
    ).toFixed(2);
    this.signedAnnualProd = (
      this.reconcileEpc.annualProduction /
      (1 - this.reconcileEpc.annualProdChangePercent / 100)
    ).toFixed();
  }

  protected ensureUserHasRole = (roleName: string) => {
    this.userService.isUserInRole(roleName).then(
      (userInRole) => {
        if (!userInRole) {
          toastr.error(`Current user does not have permission: ${roleName}.`);
          this.$state.go("main.customerv2", { igsCustomerNumber: this.costCalculationModel.customerReferenceNumber });
        }
      },
      () => {
        this.$state.go("login", { statename: "main.costCalculation" });
      },
    );
  };

  private setupPageCore = (): void => {
    if (!this.costCalculationModel) {
      this.costCalculationModel = new EpcRequest.EpcCalculationRequest();
      this.costCalculationModel.customerReferenceNumber = this.igsCustomerNumber;
    }

    this.contractAmendmentApiClient
      .hasUploadedContractAmendmentRateDocumentType(this.igsCustomerNumber)
      .then((result: boolean) => {
        this.hasUploadedContractAmendmentDocumentType = result;
        return this.pricingApiClient.getRegionAssignedToCustomer(this.igsCustomerNumber);
      })
      .then((region: Region.IRegion) => {
        this.regionAssociatedToCustomer = region;
        return this.costCalculationApiClient.getContractEpcCalculation(this.igsCustomerNumber);
      })
      .then((contractEpc) => {
        this.reconcileEpc = contractEpc;
        // need this to get annual production, system size, and yield caps
        return this.marketAssumedValuesApiClient.getMarketCapsByMarket(contractEpc.market.code);
      })
      .then((marketCaps: MarketCap.IMarketCap) => {
        this.selectedMarketCaps = marketCaps;
        return this.costCalculationApiClient.getPendingEpcCalculation(this.igsCustomerNumber);
      })
      .then((pendingCalculation) => {
        this.pendingEpc = pendingCalculation;
        this.contractAmendmentRateInProgress = !!this.pendingEpc;

        // if contract amendment in progress, populate cost calc model with pending values instead of contract
        if (this.contractAmendmentRateInProgress) {
          this.populateCostCalculationModel(this.pendingEpc);
        } else {
          this.populateCostCalculationModel(this.reconcileEpc);
        }

        this.pageLoaded = true;
        this.onPageLoaded();
      });
  };

  private verifyCustomerHasSignedContract = () => {
    this.customerApiClient
      .getCustomerEvent(this.igsCustomerNumber, Constants.contractSignedEventCode)
      .then((customerEvent: CustomerEvent.ICustomerEvent) => {
        if (customerEvent === null || customerEvent.eventOutcomeCode !== Constants.successEventOutcomeCode) {
          toastr.error("A contract must be signed before an EPC can be reconciled.");
          this.$state.go("main.customerv2", { igsCustomerNumber: this.costCalculationModel.customerReferenceNumber });
        }
      });
  };

  private verifyAgentHasReconcilePermissions = () => {
    this.userService.getCurrentUser().then((currentUser) => {
      if (!currentUser) {
        this.$state.go("login");
      } else {
        this.ensureUserHasRole(this.roleList.reconcileEpc);
      }
    });
  };

  private validateYieldCap = (): boolean => {
    if (this.selectedMarketCaps && this.selectedMarketCaps.yieldCap && this.costCalculationModel) {
      return (
        this.costCalculationModel.annualProduction / this.costCalculationModel.systemSize <=
        this.selectedMarketCaps.yieldCap
      );
    }
    return false;
  };

  private isValidLeaseCalculation = (): boolean => {
    if (!this.selectedRegionPricing) return false;

    this.regionPricingMonthlyPaymentMinimum =
      Math.floor(((this.selectedRegionPricing.rateFloor * this.costCalculationModel.annualProduction) / 12) * 100) /
      100;
    this.regionPricingMonthlyPaymentMaximum =
      Math.ceil(((this.selectedRegionPricing.rateCap * this.costCalculationModel.annualProduction) / 12) * 100) / 100;
    if (this.isLease() && this.costCalculationModel.monthlyPayment < this.regionPricingMonthlyPaymentMinimum) {
      this.leaseWarningMessage =
        "The associated monthly payment is lower than the allowed floor for the market. You will need to update the monthly payment, or the annual production.";
    } else if (this.isLease() && this.costCalculationModel.monthlyPayment > this.regionPricingMonthlyPaymentMaximum) {
      this.leaseWarningMessage =
        "The associated monthly payment is higher than the allowed cap for the market. You will need to update the monthly payment, or the annual production.";
    } else {
      this.leaseWarningMessage = null;
    }
    return true;
  };
}
